import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DEFAULT_SESSION_COLOR } from 'src/app/lib/session-color';
import { isLightBackground } from 'src/app/lib/color-contrast';
import { SESSION_PALETTE } from 'src/app/lib/session-palette';

/**
 * Seletor de cor do JEMS3.
 *
 * Existe porque o `<input type="color">` nativo abre uma janela DO NAVEGADOR:
 * o conteúdo dela é do sistema operacional, não da página, então não há como
 * incluir ali a amostra "Padrão" nem uma paleta própria. Aqui a janela é nossa
 * (o mesmo `app-popover` do `app-jems-select`), com três coisas dentro:
 * paleta, campo hexadecimal e o botão que devolve a cor padrão.
 *
 * O valor vive num FormControl, como nos demais campos do JEMS3.
 */
@Component({
  selector: 'app-color-picker',
  templateUrl: './color-picker.component.html',
  styleUrls: ['./color-picker.component.scss']
})
export class ColorPickerComponent {
  /** `<input type="color">` escondido: abre o seletor NATIVO sob demanda. */
  @ViewChild('nativeInput') nativeInput: ElementRef<HTMLInputElement>;

  @Input() formGroupController: FormGroup;
  @Input() name: string;
  /** Chave i18n do rótulo acima do campo. */
  @Input() description: string;
  /** Cor considerada "padrão" — some da paleta e ganha um botão próprio. */
  @Input() defaultColor: string = DEFAULT_SESSION_COLOR;

  /**
   * Paleta de trabalho, vinda da fonte única (`session-palette.ts`) — a mesma
   * lista que batiza as classes do programa exportado. Não é uma restrição: o
   * campo hexadecimal e o seletor nativo aceitam qualquer cor.
   */
  readonly palette: string[] = SESSION_PALETTE.map(entry => entry.hex);

  /**
   * Cores JÁ EM USO no evento — a linha "recentes" sai daqui, e não do que foi
   * clicado no seletor: experimentar uma cor e desistir não deve deixá-la na
   * lista. O consumidor passa as cores das sessões existentes.
   */
  @Input() usedColors: string[] = [];

  /**
   * Até cinco cores de sessão que NÃO estão na paleta fixa (nem são a padrão) —
   * as da paleta já estão a um clique. Preserva a ordem recebida, então cabe ao
   * consumidor mandar as mais recentes primeiro.
   */
  get recent(): string[] {
    const seen: string[] = [];
    (this.usedColors || []).forEach(color => {
      if (!this.isCustom(color)) { return; }
      const value = color.toUpperCase();
      if (seen.indexOf(value) < 0) { seen.push(value); }
    });
    return seen.slice(0, 5);
  }

  /** Fora da paleta fixa e diferente do padrão. */
  private isCustom(color: string): boolean {
    const value = (color || '').toLowerCase();
    if (!value || value === this.defaultColor.toLowerCase()) { return false; }
    return !this.palette.some(preset => preset.toLowerCase() === value);
  }

  get control() { return this.formGroupController.get(this.name); }

  get value(): string { return this.control ? this.control.value : null; }

  /** Cor de texto que contrasta com a amostra — a mesma regra do programa. */
  contrastOf(color: string): string {
    return isLightBackground(color) ? '#373734' : '#ffffff';
  }

  isSelected(color: string): boolean {
    return !!this.value && this.value.toLowerCase() === color.toLowerCase();
  }

  isDefault(): boolean {
    return this.isSelected(this.defaultColor);
  }

  pick(color: string): void {
    this.control.setValue(color);
    this.control.markAsDirty();
  }

  /**
   * Abre o seletor nativo do sistema. É o caminho para QUALQUER cor fora da
   * paleta — e é ele que traz o conta-gotas do sistema operacional no macOS e
   * no Windows, que a página não tem como desenhar.
   */
  openNative(): void {
    if (this.nativeInput) { this.nativeInput.nativeElement.click(); }
  }

  /** O nativo emite `input` a cada movimento no seletor: grava direto. */
  onNativeInput(value: string): void {
    this.pick(value);
  }

  /** Campo hexadecimal: só grava quando o texto já é uma cor completa. */
  onHexInput(raw: string): void {
    const value = (raw || '').trim();
    const hex = value.charAt(0) === '#' ? value : `#${value}`;
    if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(hex)) {
      this.pick(hex);
    }
  }
}
